import React from 'react';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {useNavigate} from 'react-router';
import {deleteWhiteboard, updateWhiteboardTitle} from '../../../firebase/firestore/whiteboards';

export default function WhiteboardHeader({
                                             whiteboardId,
                                             title,
                                             backPath = '/charts',
                                             showLoading = false,
                                             onTitleSaved,
                                             peopleMenuRef,
                                         }) {
    const navigate = useNavigate();
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [editingTitle, setEditingTitle] = React.useState(false);
    const [draftTitle, setDraftTitle] = React.useState('');
    const [deleteOpen, setDeleteOpen] = React.useState(false);
    const [renaming, setRenaming] = React.useState(false);
    const [renameError, setRenameError] = React.useState('');
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState('');
    const canRename = Boolean(whiteboardId);
    const canDelete = Boolean(whiteboardId);

    const navigateBack = () => {
        if (backPath) {
            navigate(backPath);
            return;
        }
        navigate(-1);
    };

    const startRename = () => {
        if (!canRename || renaming) return;
        setDraftTitle(title || '');
        setEditingTitle(true);
    };

    const cancelRename = () => {
        if (renaming) return;
        setEditingTitle(false);
        setDraftTitle('');
    };

    const submitRename = async () => {
        if (!canRename || renaming) return;
        try {
            setRenameError('');
            setRenaming(true);
            await updateWhiteboardTitle({whiteboardId, title: draftTitle});
            setEditingTitle(false);
            onTitleSaved?.(draftTitle);
        } catch (error) {
            setRenameError(error?.message || 'Failed to rename chart.');
            // Keep inline edit mode open so users can immediately fix and retry.
        } finally {
            setRenaming(false);
        }
    };

    const openDeleteDialog = () => {
        setMenuAnchor(null);
        setDeleteOpen(true);
    };

    const closeDeleteDialog = () => {
        if (deleting) return;
        setDeleteOpen(false);
    };

    const confirmDelete = async () => {
        if (!canDelete || deleting) return;
        try {
            setDeleteError('');
            setDeleting(true);
            await deleteWhiteboard({whiteboardId});
            setDeleteOpen(false);
            navigate(backPath || '/charts');
        } catch (error) {
            setDeleteError(error?.message || 'Failed to delete chart.');
            // Keep dialog open and let error text guide the user.
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Stack
            direction={'row'}
            alignItems={'center'}
            boxSizing='border-box'
            p={1}
            bgcolor={'white'}
            spacing={2}
            justifyContent={'space-between'}
        >
            <Stack
                direction={'row'}
                alignItems={'flex-start'}
                boxSizing="border-box"
                bgcolor={'white'}
                spacing={1}
                flexGrow={1}
                minWidth={0}
            >
                <IconButton onClick={navigateBack} sx={{mt: 0.25}}>
                    <ArrowBackOutlinedIcon/>
                </IconButton>
                <Stack spacing={0.25} flexGrow={1} minWidth={0} mr={4}>
                    {editingTitle ? (
                        <TextField
                            autoFocus
                            size="small"
                            value={draftTitle}
                            onChange={(event) => setDraftTitle(event.target.value)}
                            onBlur={submitRename}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    submitRename();
                                }
                                if (event.key === 'Escape') {
                                    event.preventDefault();
                                    cancelRename();
                                }
                            }}
                            disabled={renaming}
                            error={Boolean(renameError)}
                            sx={{flexGrow: 1}}
                        />
                    ) : (
                        <Typography
                            variant="h6"
                            onClick={startRename}
                            sx={{cursor: canRename ? 'text' : 'default'}}
                        >
                            {title}
                        </Typography>
                    )}
                </Stack>
            </Stack>

            {/* Future: additional whiteboard status/actions */}
            <Stack direction={'row'} spacing={1} alignItems={'center'}>
                {showLoading ? <CircularProgress size={18}/> : null}
                {/* People-menu avatar portal target — tldraw's WhiteboardSharePanel renders into this div */}
                <div ref={peopleMenuRef} style={{display: 'contents'}}/>
                <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)} disabled={!canDelete}>
                    <MoreVertIcon/>
                </IconButton>
            </Stack>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
            >
                <MenuItem onClick={openDeleteDialog} disabled={!canDelete}>
                    Delete
                </MenuItem>
            </Menu>

            <Dialog open={deleteOpen} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
                <DialogTitle>Delete chart?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This action cannot be undone.
                    </Typography>
                    {deleteError ? (
                        <Typography variant="body2" color="error" sx={{mt: 1}}>
                            {deleteError}
                        </Typography>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleting}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
